import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { SERVICE_ICONS } from "./icons";
import { SERVICES, IMAGES } from "../../data/content";

const EASE = [0.16, 1, 0.3, 1];
const VIEWPORT = { once: true, margin: "-80px" };

const ScanWord = ({ children, delay, className = "" }) => (
  <motion.span
    initial="hidden"
    whileInView="show"
    viewport={VIEWPORT}
    className="inline-block overflow-hidden align-top pb-[0.08em] -mb-[0.08em]"
  >
    <motion.span
      variants={{
        hidden: { y: "110%" },
        show: { y: 0, transition: { duration: 0.5, delay, ease: EASE } },
      }}
      className={`inline-block ${className}`}
    >
      {children}
    </motion.span>
  </motion.span>
);

export const ServicesStrip = () => {
  const open = (i) => {
    window.dispatchEvent(new CustomEvent("open-service", { detail: i }));
    setTimeout(() => {
      if (window.__lenis) {
        window.__lenis.scrollTo(`#service-${i}`, { offset: -110, duration: 1.4 });
      } else {
        document.getElementById(`service-${i}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 550);
  };

  return (
    <section id="services-hero" data-testid="services-strip" className="relative flex items-center overflow-hidden bg-[#172A3A] lg:min-h-[85vh]">
      <motion.img
        src={IMAGES.hvac}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover"
        initial={{ opacity: 0.72, scale: 1.025, filter: "brightness(0.68) saturate(0.75) blur(3px)" }}
        whileInView={{ opacity: 1, scale: 1, filter: "brightness(0.88) saturate(0.95) blur(0px)" }}
        viewport={VIEWPORT}
        transition={{ duration: 1.35, delay: 0.5, ease: EASE }}
      />
      <div className="absolute inset-0 bg-[#172A3A]/50" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#172A3A]/75 via-[#172A3A]/30 to-transparent" />

      <motion.span
        aria-hidden="true"
        className="absolute left-0 right-0 top-[46%] z-[5] h-[2px]"
        initial="hidden"
        whileInView="show"
        viewport={VIEWPORT}
      >
        <motion.span
          data-testid="services-scan-line"
          className="block h-full w-full origin-left bg-[#F47721]"
          variants={{
            hidden: { scaleX: 0, opacity: 0 },
            show: {
              scaleX: 1,
              opacity: [0, 1, 1, 0],
              transition: {
                scaleX: { duration: 1.25, delay: 0.5, ease: EASE },
                opacity: { duration: 1.55, delay: 0.5, times: [0, 0.08, 0.86, 1], ease: "linear" },
              },
            },
          }}
        />
      </motion.span>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-10 w-full py-20 sm:py-24 lg:py-28">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={VIEWPORT}
          transition={{ duration: 0.5, delay: 0.72, ease: EASE }}
          className="font-manrope text-[11px] font-bold uppercase tracking-[0.34em] text-[#F47721]"
          data-testid="services-hero-label"
        >
          Our Services
        </motion.p>

        <h1
          className="font-manrope font-bold text-white leading-[1.05] text-[40px] md:text-[52px] lg:text-[64px] mt-6 tracking-tight"
          data-testid="services-hero-heading"
        >
          <span className="block">
            {["ENGINEERED", "FOR", "PERFORMANCE."].map((word, i) => (
              <ScanWord key={word} delay={0.98 + i * 0.12} className={i < 2 ? "mr-[0.22em]" : ""}>
                {word}
              </ScanWord>
            ))}
          </span>
          <span className="block mt-1">
            <span className="relative inline-flex flex-wrap text-[#F47721]">
              {["BUILT", "TO", "DELIVER."].map((word, i) => (
                <ScanWord key={word} delay={1.42 + i * 0.1} className={i < 2 ? "mr-[0.22em]" : ""}>
                  {word}
                </ScanWord>
              ))}
              <motion.span
                aria-hidden="true"
                className="absolute -bottom-2 left-0 h-[2px] w-full"
                initial="hidden"
                whileInView="show"
                viewport={VIEWPORT}
              >
                <motion.span
                  data-testid="services-heading-underline"
                  className="block h-full w-full origin-left bg-[#F47721]"
                  variants={{
                    hidden: { scaleX: 0, opacity: 0 },
                    show: {
                      scaleX: 1,
                      opacity: 1,
                      transition: { duration: 0.38, delay: 1.78, ease: EASE },
                    },
                  }}
                />
              </motion.span>
            </span>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={VIEWPORT}
          transition={{ duration: 0.5, delay: 1.92, ease: EASE }}
          className="font-inter font-medium text-[17px] leading-[1.5] text-slate-200 max-w-[680px] mt-7"
          data-testid="services-hero-desc"
        >
          Integrated engineering and project execution solutions for industrial, pharmaceutical, laboratory and commercial facilities.
        </motion.p>

        <div className="relative mt-12 sm:mt-14 lg:mt-20">
          <motion.div
            aria-hidden="true"
            className="hidden lg:block absolute left-8 right-8 top-[30px] h-px"
            initial="hidden"
            whileInView="show"
            viewport={VIEWPORT}
          >
            <motion.div
              data-testid="services-discipline-line"
              className="h-full w-full origin-left bg-white/20"
              variants={{
                hidden: { scaleX: 0 },
                show: { scaleX: 1, transition: { duration: 0.75, delay: 2.2, ease: EASE } },
              }}
            />
          </motion.div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-y-10 relative">
            {SERVICES.map((s, i) => {
              const Icon = SERVICE_ICONS[s.icon];
              return (
                <motion.button
                  key={s.num}
                  data-testid={`strip-service-${i}`}
                  onClick={() => open(i)}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={VIEWPORT}
                  transition={{ duration: 0.45, delay: 2.28 + i * 0.11, ease: EASE }}
                  className="group relative flex flex-col items-center gap-4 py-2 px-2 cursor-pointer"
                >
                  <span className="w-[60px] h-[60px] rounded-full border border-white/40 bg-white/5 backdrop-blur-sm flex items-center justify-center text-white transition-colors duration-300 ease-out group-hover:bg-[#F47721] group-hover:border-[#F47721]">
                    <Icon size={24} strokeWidth={1.5} />
                  </span>
                  <span className="font-inter text-[11px] uppercase tracking-[0.14em] font-semibold text-white/80 text-center leading-snug transition-colors duration-300 ease-out group-hover:text-[#F47721]">
                    {s.short}
                  </span>
                  <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 h-[2px] w-0 bg-[#F47721] transition-[width] duration-300 ease-out group-hover:w-8" />
                </motion.button>
              );
            })}
          </div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={VIEWPORT}
        transition={{ delay: 3.15, duration: 0.45, ease: EASE }}
        className="absolute bottom-7 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2 text-slate-300"
        data-testid="services-scroll-indicator"
      >
        <span className="font-inter text-[11px] uppercase tracking-[0.25em]">Explore Our Capabilities</span>
        <ArrowDown size={15} aria-hidden="true" />
      </motion.div>
    </section>
  );
};
